import {
  EventTypeMap,
  TrackEventInput,
  Tracker as UITracker,
} from '@updater/ui-tracker';
import { useQual } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { onUpdate, offUpdate, CanonicalUserProfile } from '@updater/identity';
import { useTracking } from 'react-tracking';
import { useEnvironment } from 'services/environment';
import { appName } from './constants';
import { useCreateBaseTrackingEvent } from './createBaseTrackingEvent';
import { eventTypeMap } from './eventTypeMap';
import { QualLoadedEvent } from './QualLoadedEvent';

export function Tracker({ children }) {
  const {
    name: envName,
    eventStream,
    eventStreamToken,
    inProduction,
  } = useEnvironment();
  const { userEmail } = useQual();
  const { addExtraEventDetails } = useCreateBaseTrackingEvent();

  const [tracker, events] = useMemo(() => {
    const trackerConfig = {
      eventTypeMap,
      environment: inProduction ? envName : 'sandbox',
      destinations: {
        eventstream: {
          app: appName,
          url: eventStream,
          authorization: eventStreamToken,
        },
      },
      channel: appName,
      debug: !inProduction,
      appName,
    };
    const tracker = new UITracker<EventTypeMap>(trackerConfig);
    return [tracker, tracker.Events.marketingSites];
  }, [envName, eventStream, eventStreamToken, inProduction]);

  // QualLoadedEvent waits for this. Its effect would otherwise run before the
  // one below (child effects run before their parent's), so the event would be
  // built before identify() and go out with an empty `user`. Waiting also lets
  // useQual's effect add the qual's `uuid` first.
  const [isIdentified, setIsIdentified] = useState(false);

  // dispose of ui-tracker if/when unmounted
  useEffect(() => tracker.destroy, [tracker]);

  // Re-identify user whenever the identity profile updates
  useEffect(() => {
    const listener = (profile: CanonicalUserProfile) => {
      const { updUuid, moveId, email, ...identityRest } = profile;
      tracker.identify({
        uuid: updUuid,
        email: email ?? userEmail ?? '',
        moverId: '',
        moveId,
        ...identityRest,
      });
      setIsIdentified(true);
    };
    onUpdate(listener);
    return () => offUpdate(listener);
  }, [tracker, userEmail]);

  const { Track } = useTracking<TrackEventInput<unknown>>(
    {
      appName,
      domain: appName,
    },
    {
      dispatch: (event: any) => {
        const eventWithExtraDetails = addExtraEventDetails(event);
        if (eventWithExtraDetails.eventName) {
          const { eventName, verb, details } = eventWithExtraDetails;
          try {
            tracker.track(events[eventName][verb], details);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.info('Event error', { eventName, verb, events });
            // eslint-disable-next-line no-console
            console.error(error);
          }
        } else {
          tracker.trackUntyped(eventWithExtraDetails);
        }
      },
    }
  );

  return (
    <Track>
      {isIdentified && <QualLoadedEvent />}
      {children}
    </Track>
  );
}
